import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { Formik, FormikConfig } from 'formik';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { Phototron } from '@/common/components/phototron';
import { authenticatedRoutePaths, authenticatedRoutes } from '@/core/navigation';
import { makeGetUserData } from '@/store/authentication';
import { RedeemVoucherResponseEntity, RedeemVoucherResponseStatus } from '../../models/voucher';
import { redeemVoucher } from '../../services/voucher';
import { ControlListDefs, Form, VALIDATION_SCHEMA } from './components/form';
import { styles } from './styles';

const getUserData = makeGetUserData();

export const VoucherRedeemFormPage: FC = () => {
  const destroy$ = useRef(new Subject());
  const [t] = useTranslation();
  const userData = useSelector(getUserData);
  const history = useHistory();

  const onSubmit = useCallback<FormikConfig<ControlListDefs>['onSubmit']>(({ voucherId }, formHelpers) => {
    formHelpers.setSubmitting(true);
    redeemVoucher({ voucherId })
      .pipe(
        finalize(() => formHelpers.setSubmitting(false)),
        takeUntil(destroy$.current)
      )
      .subscribe((response: RedeemVoucherResponseEntity) => {
        const status = response.status;
        const data = response.data as any;

        formHelpers.setStatus(status);

        if (status === RedeemVoucherResponseStatus.SUCCESS && data.smartMap) {
          history.push(authenticatedRoutePaths[authenticatedRoutes.VOUCHER_MAP_REDEEM_SUCCESS]);
        } else if (status === RedeemVoucherResponseStatus.SUCCESS && data.subscription) {
          history.push(authenticatedRoutePaths[authenticatedRoutes.VOUCHER_SUBSCRIPTION_REDEEM_SUCCESS]);
        }
      });
  }, []);

  useEffect(() => {
    return () => {
      destroy$.current.next();
      destroy$.current.complete();
    };
  }, []);

  return (
    <div css={styles}>
      <Phototron />

      <h2 className='title'>{t('page.voucher.title')}</h2>
      <div className='description'>{t('page.voucher.description', { name: userData?.firstName })}</div>

      <Formik<ControlListDefs> initialValues={{ voucherId: '' }} validationSchema={VALIDATION_SCHEMA} component={Form} onSubmit={onSubmit} />
    </div>
  );
};
